<template>
  <div class="audio-container">

    <div v-if="errorMessage" class="error-box">
      {{ errorMessage }}
    </div>

    <div class="controls" v-if="canRecord">
      <button
          @click="startRecording"
          :disabled="isRecording || !canRecord"
          :class="{ 'btn-recording': isRecording }"
      >
        <span v-if="isRecording">در حال ضبط... 🎙️</span>
        <span v-else>شروع ضبط 🎤</span>
      </button>

      <button
          @click="stopRecording"
          :disabled="!isRecording"
          class="btn-stop"
      >
        پایان ضبط ⛔
      </button>
    </div>

    <!-- اندیکاتور در حال ضبط -->
    <div v-if="isRecording" class="recording-indicator">
      <div class="pulse"></div>
      <span>در حال ضبط صدا...</span>
    </div>

    <div v-if="audioUrl" class="preview">
      <audio :src="audioUrl" controls></audio>

      <div class="preview-actions">
        <button @click="emitRecordedFile" class="btn-save">
          ذخیره صدا ✔️
        </button>
        <button @click="deleteRecording" class="btn-delete">
          حذف این ضبط 🗑️
        </button>
      </div>
    </div>

    <hr />

    <div>
      <label for="upload" class="ml-1 btn btn-primary">
        <i class="fa fa-cloud-upload ml-2"></i> آپلود فایل صوتی
      </label>
      <input
          type="file"
          id="upload"
          class="d-none"
          ref="audioInput"
          accept="audio/*"
          @change="fileSelected"
      />
    </div>

    <div class="w-100 mt-2" v-if="uploadedFileName">
      <p>
        فایل انتخاب شده: **{{ uploadedFileName }}**
        <button
            @click="clearSelectedFile"
            class="btn btn-sm btn-danger ml-2"
            style="font-size: 1.2rem; line-height: 1; padding: 0.1rem 0.5rem;"
        >
          ×
        </button>
      </p>
    </div>

    <div class="w-100 pt-3 text-center">
      <button class="btn btn-success" @click="emitUploadedFile">
        ذخیره فایل
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const isRecording = ref(false)
let mediaRecorder = null
const audioChunks = []
const audioUrl = ref(null)
let recordedBlob = null

const errorMessage = ref('')
const canRecord = ref(false)
const audioInput = ref(null)

const uploadedFileName = ref(null)
let uploadedFile = null

const props = defineProps({
  file: {
    required: false
  }
})

const emit = defineEmits(['audioSelected'])

// بررسی دسترسی به میکروفون
onMounted(async () => {
  try {
    await navigator.mediaDevices.getUserMedia({ audio: true })
    canRecord.value = true
  } catch (err) {
    canRecord.value = false
    errorMessage.value =
        '⚠️ دستگاه شما قابلیت ضبط صدا ندارد یا دسترسی میکروفن مسدود است.'
  }

  if (props.file) {
    uploadedFileName.value = props.file.name
  }
})

const startRecording = async () => {
  try {
    isRecording.value = true
    audioChunks.length = 0
    errorMessage.value = ''

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    mediaRecorder = new MediaRecorder(stream)

    mediaRecorder.ondataavailable = (e) => {
      audioChunks.push(e.data)
    }

    mediaRecorder.onstop = () => {
      recordedBlob = new Blob(audioChunks, { type: 'audio/webm' })
      audioUrl.value = URL.createObjectURL(recordedBlob)

      // اختیاری: قطع کردن استریم بعد از توقف
      stream.getTracks().forEach(track => track.stop())
    }

    mediaRecorder.start()
  } catch (err) {
    errorMessage.value = '❌ هنگام شروع ضبط خطایی رخ داد. لطفا دوباره تلاش کنید.'
    isRecording.value = false
  }
}

const stopRecording = () => {
  if (mediaRecorder && isRecording.value) {
    isRecording.value = false
    mediaRecorder.stop()
    mediaRecorder = null
  }
}

const deleteRecording = () => {
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value)
  }
  audioUrl.value = null
  recordedBlob = null
  audioChunks.length = 0
}

const emitRecordedFile = () => {
  if (!recordedBlob) return
  const file = new File([recordedBlob], 'recorded-audio.webm', {
    type: 'audio/webm'
  })
  emit('audioSelected', file)
}

const fileSelected = (e) => {
  const file = e.target.files[0]
  if (file) {
    uploadedFile = file
    uploadedFileName.value = file.name
  }
}

const clearSelectedFile = () => {
  uploadedFile = null
  uploadedFileName.value = ''
  if (audioInput.value) {
    audioInput.value.value = ''
  }
}

const emitUploadedFile = () => {
  if (uploadedFile) {
    emit('audioSelected', uploadedFile)
  }
}
</script>

<style scoped>
.audio-container {
  padding: 15px;
  border: 1px solid #ccc;
  border-radius: 8px;
  width: 100%;
  direction: rtl;
}

.controls button {
  margin: 6px;
  padding: 10px 18px;
  font-size: 1.05rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-recording {
  background-color: #e63946;
  color: white;
  animation: pulse-button 1.8s infinite;
}

.btn-stop {
  background-color: #d00000;
  color: white;
}

.recording-indicator {
  margin: 12px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #e63946;
  font-weight: bold;
  font-size: 1.1rem;
}

.pulse {
  width: 14px;
  height: 14px;
  background-color: #e63946;
  border-radius: 50%;
  animation: pulse 1.4s infinite ease-in-out;
}

.preview {
  margin: 20px 0;
  text-align: center;
}

.preview audio {
  width: 100%;
  max-width: 420px;
  margin-bottom: 12px;
}

.preview-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-save {
  background-color: #2a9d8f;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn-delete {
  background-color: #606c38;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.error-box {
  background: #ffdddd;
  color: #b80000;
  border: 1px solid #ff4d4d;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 16px;
}

.w-100 {
  width: 100%;
}

.mt-2 {
  margin-top: 0.5rem;
}

.pt-3 {
  padding-top: 1rem;
}

.text-center {
  text-align: center;
}

/* انیمیشن‌ها */
@keyframes pulse {
  0% {
    transform: scale(0.9);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.5);
    opacity: 1;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.6;
  }
}

@keyframes pulse-button {
  0% {
    box-shadow: 0 0 0 0 rgba(230, 57, 70, 0.6);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(230, 57, 70, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(230, 57, 70, 0);
  }
}
</style>
